import { useState } from 'react';
import { PasswordInput } from '@/components/PasswordInput';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { PencilIcon } from '@/components/icons/PencilIcon';
import {
  useChangePasswordMutation,
  useGetCurrentUserQuery,
  useListProjectsQuery,
  useUpdateProfileMutation
} from '@/services/api';
import { checkPasswordPolicy } from '@/utils/auth';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';

const MAX_NAME_LENGTH = 80;

export function ProfilePage(): JSX.Element {
  const userQ = useGetCurrentUserQuery();
  const projectsQ = useListProjectsQuery();

  if (userQ.isLoading || projectsQ.isLoading) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }
  if (userQ.isError || !userQ.data) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <ErrorState
          title="Couldn't load your profile"
          body="The account service is unreachable."
          onRetry={() => void userQ.refetch()}
        />
      </div>
    );
  }

  const user = userQ.data;

  return (
    <>
      <h1 className="page-title">Profile</h1>

      <div className="panel">
        <h2 className="panel-title">Account</h2>
        <p className="panel-sub">Your identity and role across the factory.</p>
        <NameSection currentName={user.name ?? ''} />
        <div className="field-row">
          <div className="field">
            <label className="field-label">Email</label>
            <div className="mono">{user.email}</div>
          </div>
          <div className="field">
            <label className="field-label">Role</label>
            <div className="mono-cap">{user.role}</div>
          </div>
        </div>
      </div>

      <ChangePasswordSection />
    </>
  );
}

function ChevronIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function NameSection({ currentName }: { currentName: string }): JSX.Element {
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const isEmpty = trimmed === '';
  const tooLong = trimmed.length > MAX_NAME_LENGTH;
  const changed = trimmed !== currentName.trim();
  const canSave = changed && !isEmpty && !tooLong && !isLoading;

  function startEdit(): void {
    setName(currentName);
    setError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setName(currentName);
    setError(null);
    setEditing(false);
  }

  async function onSave(): Promise<void> {
    setError(null);
    try {
      await updateProfile({ display_name: trimmed }).unwrap();
      toast.success('Name updated.');
      setEditing(false);
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <div className="field">
      <label className="field-label" htmlFor={editing ? 'profile-name' : undefined}>
        Name
      </label>
      {editing ? (
        <>
          <div className="field-inline">
            <input
              id="profile-name"
              className="input"
              placeholder="Your name"
              value={name}
              autoFocus
              maxLength={MAX_NAME_LENGTH + 20}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSave) void onSave();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
            <button className="btn primary sm" disabled={!canSave} onClick={() => void onSave()}>
              {isLoading ? 'Saving…' : 'Save'}
            </button>
            <button className="btn ghost sm" disabled={isLoading} onClick={cancelEdit}>
              Cancel
            </button>
          </div>
          {isEmpty ? (
            <div className="field-err">Name is required.</div>
          ) : tooLong ? (
            <div className="field-err">Name must be {MAX_NAME_LENGTH} characters or fewer.</div>
          ) : error ? (
            <div className="field-err" role="alert">
              {error}
            </div>
          ) : null}
        </>
      ) : (
        <div className="name-display">
          <span>{currentName.trim() === '' ? '—' : currentName}</span>
          <button
            className="icon-edit-btn"
            onClick={startEdit}
            aria-label="Edit name"
            title="Edit name"
          >
            <PencilIcon />
          </button>
        </div>
      )}
    </div>
  );
}

function ChangePasswordSection(): JSX.Element {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
  }

  function close(): void {
    reset();
    setOpen(false);
  }

  const pwPolicy = checkPasswordPolicy(next);
  const passwordsMatch = confirm === '' || next === confirm;
  const canSubmit =
    current !== '' && pwPolicy.ok && confirm !== '' && next === confirm && !isLoading;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    try {
      await changePassword({ current_password: current, new_password: next }).unwrap();
      setSuccess(true);
      reset();
      setOpen(false);
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <div className="panel" id="change-password">
      <button
        type="button"
        className="panel-head-row section-toggle"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <div>
          <h2 className="panel-title">Change password</h2>
          <p className="panel-sub">
            Changing your password signs out your other active sessions; this session continues.
          </p>
        </div>
        <span className={`chevron ${open ? 'open' : ''}`} aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>

      {success && (
        <div className="banner success" role="status">
          <span className="banner-icon">✓</span>
          <span>
            Password changed. Your other active sessions have been signed out. This session continues.
          </span>
        </div>
      )}

      {open && (
      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="cp-current">
            Current password
          </label>
          <PasswordInput
            allowAutofill
            id="cp-current"
            invalid={!!error}
            autoComplete="current-password"
            placeholder="••••••••••••"
            value={current}
            onChange={setCurrent}
          />
          {error && <div className="field-err">{error}</div>}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="cp-new">
            New password
          </label>
          <PasswordInput
            allowAutofill
            id="cp-new"
            invalid={next !== '' && !pwPolicy.ok}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={next}
            onChange={setNext}
          />
          {next !== '' && !pwPolicy.ok ? (
            <div className="field-err">{pwPolicy.reason}</div>
          ) : (
            <div className="field-help">Minimum 12 characters, mixed character classes.</div>
          )}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="cp-confirm">
            Confirm new password
          </label>
          <PasswordInput
            allowAutofill
            id="cp-confirm"
            invalid={!passwordsMatch}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={confirm}
            onChange={setConfirm}
          />
          {!passwordsMatch && <div className="field-err">Passwords do not match.</div>}
        </div>
        <div className="actions">
          <button className="btn ghost" type="button" disabled={isLoading} onClick={close}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={!canSubmit}>
            {isLoading ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
